import { action, observable, makeObservable } from 'mobx';
import * as React from 'react';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IPricingStrategyProfileEntityAttributes extends IModelAttributes {
	enabled: boolean;
	name: string;
	description: string;

	passengerStrategyId: string;
	passengerStrategy?: Models.PricingStrategyEntity | Models.IPricingStrategyEntityAttributes;

	vehicleStrategyId: string;
	vehicleStrategy?: Models.PricingStrategyEntity | Models.IPricingStrategyEntityAttributes;

	freightStrategyId: string;
	freightStrategy?: Models.PricingStrategyEntity | Models.IPricingStrategyEntityAttributes;

	configGenerals: Array<Models.ConfigGeneralEntity | Models.IConfigGeneralEntityAttributes>;
	routes: Array<Models.RouteEntity | Models.IRouteEntityAttributes>;
	ferryTrips: Array<Models.FerryTripEntity | Models.IFerryTripEntityAttributes>;
	users: Array<Models.UserEntity | Models.IUserEntityAttributes>;
	defaultPricings: Array<Models.DefaultPricingEntity | Models.IDefaultPricingEntityAttributes>;
}

@entity('PricingStrategyProfileEntity', 'PricingStrategyProfile')
export default class PricingStrategyProfileEntity extends Model implements IPricingStrategyProfileEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute()
	public enabled: boolean;

	@observable
	@attribute()
	public name: string;

	@observable
	@attribute()
	public description: string;

	@observable
	@attribute()
	public passengerStrategyId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public passengerStrategy?: Models.PricingStrategyEntity;

	@observable
	@attribute()
	public vehicleStrategyId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public vehicleStrategy?: Models.PricingStrategyEntity;

	@observable
	@attribute()
	public freightStrategyId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightStrategy?: Models.PricingStrategyEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public configGenerals: Models.ConfigGeneralEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public routes: Models.RouteEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferryTrips: Models.FerryTripEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public users: Models.UserEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public defaultPricings: Models.DefaultPricingEntity[] = [];

	constructor(attributes?: Partial<IPricingStrategyProfileEntityAttributes>) {
		super(attributes);

		this.configGenerals = [];
		this.routes = [];
		this.ferryTrips = [];
		this.users = [];
		this.defaultPricings = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPricingStrategyProfileEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.enabled !== undefined) {
				this.enabled = attributes.enabled;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.passengerStrategyId !== undefined) {
				this.passengerStrategyId = attributes.passengerStrategyId;
			}
			if (attributes.vehicleStrategyId !== undefined) {
				this.vehicleStrategyId = attributes.vehicleStrategyId;
			}
			if (attributes.freightStrategyId !== undefined) {
				this.freightStrategyId = attributes.freightStrategyId;
			}
			if (attributes.passengerStrategy !== undefined) {
				if (attributes.passengerStrategy === null) {
					this.passengerStrategy = attributes.passengerStrategy;
				} else if (attributes.passengerStrategy instanceof Models.PricingStrategyEntity) {
					this.passengerStrategy = attributes.passengerStrategy;
				} else {
					this.passengerStrategy = new Models.PricingStrategyEntity(attributes.passengerStrategy);
				}
			}
			if (attributes.vehicleStrategy !== undefined) {
				if (attributes.vehicleStrategy === null) {
					this.vehicleStrategy = attributes.vehicleStrategy;
				} else if (attributes.vehicleStrategy instanceof Models.PricingStrategyEntity) {
					this.vehicleStrategy = attributes.vehicleStrategy;
				} else {
					this.vehicleStrategy = new Models.PricingStrategyEntity(attributes.vehicleStrategy);
				}
			}
			if (attributes.freightStrategy !== undefined) {
				if (attributes.freightStrategy === null) {
					this.freightStrategy = attributes.freightStrategy;
				} else if (attributes.freightStrategy instanceof Models.PricingStrategyEntity) {
					this.freightStrategy = attributes.freightStrategy;
				} else {
					this.freightStrategy = new Models.PricingStrategyEntity(attributes.freightStrategy);
				}
			}
			if (attributes.configGenerals !== undefined && Array.isArray(attributes.configGenerals)) {
				if (!this.configGenerals) {
					this.configGenerals = [];
				}
				for (const model of attributes.configGenerals) {
					if (model instanceof Models.ConfigGeneralEntity) {
						this.configGenerals.push(model);
					} else {
						this.configGenerals.push(new Models.ConfigGeneralEntity(model));
					}
				}
			}
			if (attributes.routes !== undefined && Array.isArray(attributes.routes)) {
				if (!this.routes) {
					this.routes = [];
				}
				for (const model of attributes.routes) {
					if (model instanceof Models.RouteEntity) {
						this.routes.push(model);
					} else {
						this.routes.push(new Models.RouteEntity(model));
					}
				}
			}
			if (attributes.ferryTrips !== undefined && Array.isArray(attributes.ferryTrips)) {
				if (!this.ferryTrips) {
					this.ferryTrips = [];
				}
				for (const model of attributes.ferryTrips) {
					if (model instanceof Models.FerryTripEntity) {
						this.ferryTrips.push(model);
					} else {
						this.ferryTrips.push(new Models.FerryTripEntity(model));
					}
				}
			}
			if (attributes.users !== undefined && Array.isArray(attributes.users)) {
				if (!this.users) {
					this.users = [];
				}
				for (const model of attributes.users) {
					if (model instanceof Models.UserEntity) {
						this.users.push(model);
					} else {
						this.users.push(new Models.UserEntity(model));
					}
				}
			}
			if (attributes.defaultPricings !== undefined && Array.isArray(attributes.defaultPricings)) {
				if (!this.defaultPricings) {
					this.defaultPricings = [];
				}
				for (const model of attributes.defaultPricings) {
					if (model instanceof Models.DefaultPricingEntity) {
						this.defaultPricings.push(model);
					} else {
						this.defaultPricings.push(new Models.DefaultPricingEntity(model));
					}
				}
			}
		}
	}
}
